<template>
  <div class="event-settings-page">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">&larr;</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>

    <div class="page-header">
      <p class="eyebrow">{{ $adminT('adminAll.event_settings_b04cf5', 'EVENT SETTINGS') }}</p>
      <h1>{{ $adminT('adminAll.event_settings_c710a5', 'Event Settings') }}</h1>
      <p class="lede">
        {{ $adminT('adminAll.find_an_event_by_id_or_title_and_manage_superadmin_only_in_cd9b7f', 'Find an event by ID or title and manage superadmin-only integration settings.') }}
      </p>
    </div>

    <section class="card">
      <p class="section-title">{{ $adminT('adminAll.find_event_d837cf', 'Find event') }}</p>
      <form class="search-row" @submit.prevent="searchEvents">
        <div class="field">
          <label for="admin-event-search">{{ $adminT('adminAll.event_id_or_title_0fc37f', 'Event ID or title') }}</label>
          <input
            id="admin-event-search"
            v-model.trim="query"
            type="search"
            :placeholder="$adminT('adminAll.for_example_2316_or_test_event_vat_a29ad5', 'For example: 2316 or Test event VAT')"
          >
        </div>
        <main-button
          class=""
          depressed
          :loading="loading"
          :disabled="loading || !canSearch"
          @click.native="searchEvents"
        >
          {{ $adminT('adminAll.search_events_a5a346', 'Search events') }}
        </main-button>
      </form>
      <div v-if="searchError" class="alert error">{{ adminAllMessage(searchError) }}</div>
    </section>

    <div class="content-grid">
      <section class="card">
        <p class="section-title">{{ $adminT('adminAll.events_c5497b', 'Events') }}</p>
        <div v-if="loading" class="empty-state">{{ $adminT('adminAll.loading_events_f691a4', 'Loading events...') }}</div>
        <div v-else-if="hasSearched && !events.length" class="empty-state">{{ $adminT('adminAll.no_events_found_00106d', 'No events found.') }}</div>
        <div v-else-if="!hasSearched" class="empty-state">{{ $adminT('adminAll.search_for_an_event_to_continue_a334cc', 'Search for an event to continue.') }}</div>
        <template v-else>
          <button
            v-for="event in events"
            :key="event.id"
            type="button"
            class="event-row"
            :class="{ 'event-row--selected': selectedEvent && selectedEvent.id === event.id }"
            @click="selectEvent(event)"
          >
            <span>
              <strong>{{ adminAllMessage(event.title) }}</strong>
              <small>{{ $adminT('adminAll.id_89f89c', 'ID') }} {{ event.id }} &middot; {{ formatDateRange(event) }}</small>
            </span>
            <span class="event-meta">
              {{ adminAllLabel(event.status) }}
              <span v-if="isSkidataEnabled(event)" class="status-badge">{{ $adminT('adminAll.skidata_d860a7', 'SKIDATA') }}</span>
            </span>
          </button>
        </template>
      </section>

      <section class="card">
        <p class="section-title">{{ $adminT('adminAll.selected_event_653ca8', 'Selected event') }}</p>
        <div v-if="!selectedEvent" class="empty-state">{{ $adminT('adminAll.select_an_event_to_edit_settings_a657b5', 'Select an event to edit settings.') }}</div>
        <template v-else>
          <div class="details-grid">
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.event_ad8919', 'Event') }}</p>
              <p class="detail-value">{{ adminAllMessage(selectedEvent.title) }}</p>
            </div>
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.id_status_55b283', 'ID / status') }}</p>
              <p class="detail-value">{{ selectedEvent.id }} &middot; {{ adminAllLabel(selectedEvent.status) }}</p>
            </div>
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.event_period_aeed51', 'Event period') }}</p>
              <p class="detail-value">{{ formatDateRange(selectedEvent) }}</p>
            </div>
          </div>

          <skidata-event-toggle
            :key="selectedEvent.id"
            :event-id="selectedEvent.id"
            :enabled="isSkidataEnabled(selectedEvent)"
            @updated="updateSelectedEventSkidata"
          />
          <shared-table-settings :event-id="selectedEvent.id" />
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import { SEARCH_EVENTS_SUPERADMIN } from '@/graphql/event.graphql'
import SkidataEventToggle from '@/components/events/SkidataEventToggle.vue'
import SharedTableSettings from '@/components/admin/SharedTableSettings.vue'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'admin-event-settings',
  components: { SkidataEventToggle, SharedTableSettings },
  data() {
    return {
      query: '',
      loading: false,
      hasSearched: false,
      searchError: null,
      events: [],
      selectedEvent: null,
    }
  },
  computed: {
    canSearch() {
      const term = this.query.trim()

      return /^[0-9]+$/.test(term) || term.length >= 2
    },
  },
  methods: {
    async searchEvents() {
      if (!this.canSearch || this.loading) return

      this.loading = true
      this.searchError = null
      this.hasSearched = true
      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_EVENTS_SUPERADMIN,
          variables: { q: this.query.trim(), limit: 20 },
          fetchPolicy: 'network-only',
        })
        this.events = (data && data.searchEventsSuperadmin) || []
        this.selectedEvent = null
      } catch (error) {
        this.events = []
        this.selectedEvent = null
        const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0]
        this.searchError =
          (graphQLError && graphQLError.message) ||
          (error && error.message) ||
          this.adminAllNotice('Failed to search events.')
      } finally {
        this.loading = false
      }
    },
    selectEvent(event) {
      this.selectedEvent = event
    },
    isSkidataEnabled(event) {
      return Boolean(event && event.metadata && event.metadata.skidata && event.metadata.skidata.enabled)
    },
    updateSelectedEventSkidata(enabled) {
      const currentMetadata = this.selectedEvent.metadata || {}
      const updatedEvent = {
        ...this.selectedEvent,
        metadata: {
          ...currentMetadata,
          skidata: {
            ...(currentMetadata.skidata || {}),
            enabled,
          },
        },
      }
      this.selectedEvent = updatedEvent
      this.events = this.events.map(event => (event.id === updatedEvent.id ? updatedEvent : event))
    },
    formatDateRange(event) {
      if (!event || !event.from || !event.to) return '-'

      return `${this.formatDate(event.from)} - ${this.formatDate(event.to)}`
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(value))
    },
  },
}
</script>

<style scoped>
.event-settings-page {
  max-width: 1280px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 2rem;
  color: #111827;
}

.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: #3d5b9a;
  text-decoration: none;
}

.page-header {
  margin-bottom: 1.5rem;
}

.eyebrow,
.section-title,
.detail-label {
  margin: 0 0 0.35rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 0.5rem;
  color: #0f172a;
  font-size: 1.875rem;
}

.lede {
  margin: 0;
  color: #4b5563;
}

.card {
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.search-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
}

.field {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.4rem;
}

.field label {
  font-size: 0.875rem;
  font-weight: 600;
}

.field input {
  padding: 0.65rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.event-row {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem;
  border: 1px solid transparent;
  border-bottom-color: #e2e8f0;
  background: transparent;
  color: #111827;
  text-align: left;
  cursor: pointer;
}

.event-row--selected {
  border-color: #ff4c00;
  border-radius: 8px;
  background: #fff7ed;
}

.event-row strong,
.event-row small {
  display: block;
}

.event-row small,
.event-meta,
.empty-state {
  color: #6b7280;
  font-size: 0.875rem;
}

.event-meta {
  text-align: right;
}

.status-badge {
  display: block;
  margin-top: 0.25rem;
  color: #047857;
  font-weight: 700;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.detail-card {
  padding: 0.8rem;
  border-radius: 8px;
  background: #f8fafc;
}

.detail-value {
  margin: 0;
  word-break: break-word;
}

.empty-state {
  padding: 1rem 0;
}

.alert {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
}

.alert.error {
  background: #fef2f2;
  color: #b91c1c;
}

@media (max-width: 800px) {
  .content-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .search-row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
